import { GameEvent, User } from '@/types'
import { SignedFetchRequest } from '@/utils/requests/types'

import { MessageAction, OutcomingMessage } from '../main-frame/message-types'

type GetUserFunction = () => Promise<User | null>

// Games that break with non-empty user profile
const GUEST_PROFILE_DISABLED_GAMES = [] as string[]

export interface UserServiceOptions {
    authorizeUser: GetUserFunction
    gameHru: string
    auid: string
    signedRequest: SignedFetchRequest
    isAuthEnabled?: boolean
}

export interface GameUserProfile {
    name: string
    id: string
    photos: string[]
    goldBalance: number
    isAuthorized: boolean
    jwt?: string
}

const unauthorized = {
    name: null,
    id: null,
    photos: [],
    isAuthorized: false,
    goldBalance: 0,
}

const convertProfileToMessage = <T>(payload: T) => ({
    action: MessageAction.userInfo,
    type: 'user' as OutcomingMessage['type'],
    payload,
})

const attachEventToMessage = (event: GameEvent, message: Omit<OutcomingMessage, 'originalMessageEvent'>) => ({
    ...message,
    originalMessageEvent: event as GameEvent,
})

const getGuestProfile = (uuid: string) => {
    return {
        name: `Guest-${uuid.split('-')[0]}`,
        id: uuid,
        photos: [],
        isAuthorized: false,
        goldBalance: 0,
    }
}

export class UserService {
    private authorizeUser: UserServiceOptions['authorizeUser']
    private anonymousProfile: GameUserProfile | typeof unauthorized
    private signedRequest: SignedFetchRequest
    private gameHru: string
    private isAuthEnabled: boolean

    constructor(options: UserServiceOptions) {
        this.authorizeUser = options.authorizeUser
        this.signedRequest = options.signedRequest
        this.gameHru = options.gameHru
        this.isAuthEnabled = options.isAuthEnabled ?? true

        this.anonymousProfile = GUEST_PROFILE_DISABLED_GAMES.includes(options.gameHru)
            ? unauthorized
            : getGuestProfile(options.auid)
    }

    async getUser() {
        if (!this.isAuthEnabled) {
            return convertProfileToMessage(this.anonymousProfile)
        }

        const { data } = await this.signedRequest<GameUserProfile>(`/api/v1/user/gameProfile?gameHru=${this.gameHru}`)

        if (!data) {
            return convertProfileToMessage(this.anonymousProfile)
        }

        return convertProfileToMessage<GameUserProfile>(data)
    }

    async handleMessage(event: GameEvent, onLogin: () => void) {
        let userInfo: Awaited<ReturnType<typeof this.getUser>>
        switch (event.data.action) {
            case MessageAction.getUser:
                return attachEventToMessage(event, await this.getUser())

            case MessageAction.authorizeUser:
                userInfo = await this.getUser()
                if (this.isAuthEnabled && !userInfo.payload.isAuthorized) {
                    try {
                        await this.authorizeUser()
                        userInfo = await this.getUser()
                        if (userInfo) {
                            onLogin()
                        }
                    } catch (e) {
                        console.info(e)
                        userInfo = convertProfileToMessage(this.anonymousProfile)
                    }
                }

                return attachEventToMessage(event, userInfo)

            default:
                return attachEventToMessage(event, {
                    action: MessageAction.unknownMessageType,
                    payload: {
                        error: `Unknown message action "${event.data.action}"`,
                    },
                    type: 'error',
                })
        }
    }
}
